import { Box, Button } from "@mui/material";
import { ROLE_LABEL, categoryColor, plural, sessionLabel, shortDate } from "./scheduleModel";
import { Note, FactLine, CategoryTile, ICONS, outlineBtnSx, TOKENS as T } from "./scheduleUi";

/**
 * What the coordinator said when they turned the request down: each distinct comment once (a whole-class rejection
 * shares one), with the sessions it covers when the row holds different comments, and who decided and when.
 */
export function rejectionNotes(openings) {
  const byText = new Map();
  openings.filter((o) => o.myDecisionNote).forEach((o) => {
    if (!byText.has(o.myDecisionNote)) byText.set(o.myDecisionNote, []);
    byText.get(o.myDecisionNote).push(o);
  });
  const decided = openings.find((o) => o.myDecidedByName || o.myDecidedAt) || {};
  const when = decided.myDecidedAt ? new Date(decided.myDecidedAt).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "";
  return {
    items: [...byText.entries()].map(([text, list]) => ({ text, dates: list.map((o) => shortDate(o.sessionDate)) })),
    by: [decided.myDecidedByName, when].filter(Boolean).join(", "),
  };
}

/** One request: pending (can withdraw) or not approved. Grouped per class + role, so it may span several sessions. */
export default function RequestRow({ request, busy, onWithdraw, onDetails }) {
  const { openings, status } = request;
  const pending = status === "PENDING";
  const count = openings.length;
  const rejection = !pending && !request.lost ? rejectionNotes(openings) : { items: [], by: "" };
  return (
    <Box data-testid={`request-${request.id}`}
      sx={{ display: "grid", gridTemplateColumns: { xs: "44px minmax(0,1fr)", md: "44px minmax(0,1fr) auto" }, gap: "18px", alignItems: "center", p: "16px 20px",
        "& + &": { borderTop: `1px solid ${T.hairline}` } }}>
      <CategoryTile color={categoryColor(request.category)} />
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "baseline", gap: "10px", flexWrap: "wrap", mb: "2px" }}>
          <Box component="b" sx={{ fontSize: 16, fontWeight: 600 }}>{request.courseTitle}</Box>
          {request.courseNumber && <Box component="span" sx={{ fontFamily: "var(--font-mono)", fontSize: 12, color: T.muted }}>{request.courseNumber}</Box>}
        </Box>
        <FactLine>
          <span>{ROLE_LABEL[request.role] || request.role}</span>
          <Note icon={count > 1 ? ICONS.layers : ICONS.list}>{count > 1 ? plural(count, "session") : "Session"}</Note>
        </FactLine>
        <Box component="ul" sx={{ m: "8px 0 0", p: 0, listStyle: "none", display: "grid", gap: "2px", fontSize: 13 }}>
          {openings.map((o) => (
            <Box key={o.id} component="li" sx={{ display: "flex", flexWrap: "wrap", gap: "0 8px" }}>
              <Box component="b" sx={{ fontWeight: 600 }}>{shortDate(o.sessionDate)}</Box>
              {sessionLabel(o) && <Box component="span" sx={{ color: T.muted }}>{sessionLabel(o)}</Box>}
            </Box>
          ))}
        </Box>
        {rejection.items.length > 0 && (
          <Box data-testid={`request-note-${request.id}`}
            sx={{ mt: "10px", p: "10px 12px", borderRadius: "8px", bgcolor: "rgba(180,35,24,0.08)", fontSize: 13 }}>
            <Box component="b" sx={{ display: "block", fontSize: 11, letterSpacing: ".06em", textTransform: "uppercase", color: T.danger, mb: "2px" }}>
              Coordinator&apos;s comments
            </Box>
            {rejection.items.map((n) => (
              <Box key={n.text} sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                {rejection.items.length > 1 && <Box component="b" sx={{ fontWeight: 600 }}>{n.dates.join(", ")}: </Box>}
                {n.text}
              </Box>
            ))}
            {rejection.by && <Box component="small" sx={{ display: "block", mt: "4px", color: T.muted, fontSize: 12 }}>{rejection.by}</Box>}
          </Box>
        )}
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: { xs: "flex-start", md: "flex-end" }, gap: "6px", gridColumn: { xs: 2, md: "auto" } }}>
        {request.lost
          ? <Note tone="bad" sx={{ fontWeight: 600, fontSize: 13 }}>Slot filled</Note>
          : pending
            ? <Note tone="warn" icon={ICONS.clock} sx={{ fontWeight: 600, fontSize: 13 }}>Pending approval</Note>
            : <Note tone="bad" sx={{ fontWeight: 600, fontSize: 13 }}>Not approved</Note>}
        {request.lost && <Box component="small" sx={{ fontSize: 12, color: T.muted, maxWidth: 220, textAlign: { md: "right" } }}>Another instructor was chosen. Withdraw to clear it.</Box>}
        <Box sx={{ display: "flex", gap: "6px" }}>
          <Button aria-label={`Details for ${request.courseTitle}`} sx={outlineBtnSx}
            onClick={() => onDetails({ classId: openings[0].classId, courseTitle: request.courseTitle, courseNumber: request.courseNumber,
              category: request.category, role: request.role, sessionIds: openings.map((o) => o.sessionId) })}>
            Details
          </Button>
          {pending && (
            <Button onClick={() => onWithdraw(request)} disabled={busy} sx={outlineBtnSx}>Withdraw sign-up</Button>
          )}
        </Box>
      </Box>
    </Box>
  );
}
